import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { AlliedService, AlliedServiceCreate, AlliedServiceFilter, AlliedServiceUpdate } from '@core/types/allied-service.type';

@Injectable({ providedIn: 'root' })
export class AlliedServiceService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/allied-services`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<AlliedService>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as AlliedServiceFilter;
    if (filter.isActive !== undefined) params = params.set('filter.isActive', String(filter.isActive));
    if (filter.category) params = params.set('filter.category', filter.category);

    return this.http.get<ApiResponse<PaginationResult<AlliedService>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading allied services')),
    );
  }

  get(id: string): Observable<ApiResponse<AlliedService>> {
    return this.http.get<ApiResponse<AlliedService>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading allied service')),
    );
  }

  create(data: AlliedServiceCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating allied service')),
    );
  }

  update(data: AlliedServiceUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating allied service')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting allied service')),
    );
  }
}

